import React, { useState, useEffect } from 'react';
import PlayerService from '../../services/PlayerService';
import NotificationManager from '../../utils/NotificationManager';
import ReferralNetworkMap from './ReferralNetworkMap';
import QubitChart from './QubitChart';
import './AdminDashboard.css';

const AdminDashboard = ({ onClose, currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [allPlayers, setAllPlayers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalQubits: 0,
    totalReferrals: 0,
    activeUsers24h: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); // qubits, rank, username, date
  const [selectedUser, setSelectedUser] = useState(null);
  const [showGlobalMap, setShowGlobalMap] = useState(false);
  const [showQubitChart, setShowQubitChart] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);

      // Fetch all players
      const players = await PlayerService.getAllPlayers();
      setAllPlayers(players);

      // Calculate statistics
      const totalQubits = players.reduce((sum, p) => sum + (p.qubits || 0), 0);
      const totalReferrals = players.reduce((sum, p) => sum + (p.referral?.count || 0), 0);

      // Count active users in last 24h
      const now = Date.now();
      const activeUsers = players.filter(p => {
        if (!p.lastUpdated) return false;
        const lastUpdate = new Date(p.lastUpdated).getTime();
        return (now - lastUpdate) < 86400000; // 24 hours
      }).length;

      setStats({
        totalUsers: players.length,
        totalQubits,
        totalReferrals,
        activeUsers24h: activeUsers
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading admin data:', error);
      NotificationManager.notify('Failed to load admin data');
      setLoading(false);
    }
  };

  const getRankColor = (rank) => {
    const rankColors = {
      '0': "#A9A9A9",
      '1': "#00BFFF",
      '2': "#9370DB",
      '3': "#FFD700",
      '4': "#008080",
      '5': "#8A2BE2",
      '6': "#FD6907"
    };
    return rankColors[rank] || "#33ff33";
  };

  const getRankName = (rank) => {
    const rankNames = {
      '0': "Signal Seeker",
      '1': "Void Aligned",
      '2': "Sentient",
      '3': "Frequency Specialist",
      '4': "Echo Bound",
      '5': "Resonance Manipulator",
      '6': "VOID"
    };
    return rankNames[rank] || "Unknown";
  };

  const filteredAndSortedPlayers = () => {
    let filtered = allPlayers;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.discordUsername?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'qubits':
          return (b.qubits || 0) - (a.qubits || 0);
        case 'rank':
          const rankA = parseInt(a.rank || '0');
          const rankB = parseInt(b.rank || '0');
          return rankB - rankA; // Higher rank first
        case 'username':
          return (a.discordUsername || '').localeCompare(b.discordUsername || '');
        case 'date':
          const dateA = new Date(a.lastUpdated || 0).getTime();
          const dateB = new Date(b.lastUpdated || 0).getTime();
          return dateB - dateA;
        default:
          return 0;
      }
    });

    return filtered;
  };

  if (loading) {
    return (
      <div className="admin-dashboard-modal">
        <div className="admin-backdrop" onClick={onClose} />
        <div className="admin-content loading">
          <div className="loading-text">LOADING ADMIN DASHBOARD...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-modal">
      <div className="admin-backdrop" onClick={onClose} />
      <div className="admin-content">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
            <h2>ADMIN CONTROL PANEL</h2>
          </div>
          <button onClick={onClose} className="admin-close-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
            CLOSE
          </button>
        </div>

        {/* Statistics Grid */}
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
            <div className="stat-label">TOTAL USERS</div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 6v6l4 2"></path>
              </svg>
            </div>
            <div className="stat-value">{stats.activeUsers24h.toLocaleString()}</div>
            <div className="stat-label">ACTIVE (24H)</div>
          </div>

          <div
            className="admin-stat-card clickable"
            onClick={() => setShowQubitChart(true)}
            role="button"
            tabIndex={0}
          >
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
            </div>
            <div className="stat-value">{stats.totalQubits.toLocaleString()}</div>
            <div className="stat-label">TOTAL QUBITS</div>
            <div className="stat-action">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="3" x2="21" y2="21"></line>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
              VIEW CHART
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-value">{stats.totalReferrals.toLocaleString()}</div>
            <div className="stat-label">TOTAL REFERRALS</div>
          </div>
        </div>

        {/* User Management Section */}
        <div className="admin-users-section">
          <div className="admin-section-header">
            <h3>USER MANAGEMENT</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowGlobalMap(true)} className="refresh-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                REFERRAL MAP
              </button>
              <button onClick={loadAdminData} className="refresh-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
                REFRESH
              </button>
            </div>
          </div>

          {/* Search and Filter Controls */}
          <div className="admin-controls">
            <div className="search-container">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#33ff33" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search by username or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input"
              />
            </div>

            <div className="sort-controls">
              <label>SORT BY:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="admin-select">
                <option value="qubits">Qubits (High to Low)</option>
                <option value="rank">Rank (High to Low)</option>
                <option value="username">Username (A-Z)</option>
                <option value="date">Last Active</option>
              </select>
            </div>
          </div>

          {/* User List */}
          <div className="admin-users-list">
            <div className="users-list-header">
              <div className="col-username">USERNAME</div>
              <div className="col-rank">RANK</div>
              <div className="col-qubits">QUBITS</div>
              <div className="col-referrals">REFS</div>
              <div className="col-admin">ADMIN</div>
              <div className="col-actions">ACTIONS</div>
            </div>

            <div className="users-list-body">
              {filteredAndSortedPlayers().map((player) => (
                <div key={player.id} className="user-row">
                  <div className="col-username">
                    <div className="username-cell">
                      <span className="username-text">{player.discordUsername || 'Unknown'}</span>
                      <span className="user-id">{player.id?.substring(0, 8)}...</span>
                    </div>
                  </div>
                  <div className="col-rank">
                    <span
                      className="rank-badge"
                      style={{
                        color: getRankColor(player.rank),
                        borderColor: getRankColor(player.rank)
                      }}
                    >
                      R{player.rank || '0'}
                    </span>
                  </div>
                  <div className="col-qubits">{(player.qubits || 0).toLocaleString()}</div>
                  <div className="col-referrals">{player.referral?.count || 0}</div>
                  <div className="col-admin">
                    {player.isAdmin ? (
                      <span className="admin-badge">ADMIN</span>
                    ) : (
                      <span className="admin-badge inactive">NO</span>
                    )}
                  </div>
                  <div className="col-actions">
                    <button
                      onClick={() => setSelectedUser(player)}
                      className="view-btn"
                    >
                      VIEW
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="users-list-footer">
            Showing {filteredAndSortedPlayers().length} of {allPlayers.length} users
          </div>
        </div>

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="user-detail-modal">
            <div className="user-detail-backdrop" onClick={() => setSelectedUser(null)} />
            <div className="user-detail-content">
              <div className="user-detail-header">
                <h3>USER DETAILS</h3>
                <button onClick={() => setSelectedUser(null)} className="close-detail-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <div className="user-detail-body">
                <div className="detail-section">
                  <h4>PROFILE</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Username:</span>
                      <span className="detail-value">{selectedUser.discordUsername}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Discord ID:</span>
                      <span className="detail-value">{selectedUser.id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Rank:</span>
                      <span className="detail-value" style={{ color: getRankColor(selectedUser.rank) }}>
                        {getRankName(selectedUser.rank)} (R{selectedUser.rank})
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Qubits:</span>
                      <span className="detail-value">{(selectedUser.qubits || 0).toLocaleString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Admin:</span>
                      <span className="detail-value">{selectedUser.isAdmin ? 'YES' : 'NO'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated:</span>
                      <span className="detail-value">
                        {selectedUser.lastUpdated ? new Date(selectedUser.lastUpdated).toLocaleString() : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>REFERRAL DATA</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Referral Code:</span>
                      <span className="detail-value">{selectedUser.referral?.code || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Direct Referrals:</span>
                      <span className="detail-value">{selectedUser.referral?.count || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Total Earned:</span>
                      <span className="detail-value">{(selectedUser.referral?.totalEarned || 0).toLocaleString()} qubits</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Referred By:</span>
                      <span className="detail-value">{selectedUser.referredBy || 'None'}</span>
                    </div>
                  </div>
                </div>

                {selectedUser.qubitWindow && (
                  <div className="detail-section">
                    <h4>QUBIT WINDOW</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Window Start:</span>
                        <span className="detail-value">
                          {new Date(selectedUser.qubitWindow.startTime).toLocaleString()}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Total in Window:</span>
                        <span className="detail-value">
                          {selectedUser.qubitWindow.totalQubits}/20,000
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Global Referral Network Map */}
        {showGlobalMap && (
          <ReferralNetworkMap
            onClose={() => setShowGlobalMap(false)}
            currentUserId={currentUser?.playerId}
          />
        )}

        {/* Qubit Chart Modal */}
        {showQubitChart && (
          <div className="chart-modal-overlay">
            <div className="chart-modal-backdrop" onClick={() => setShowQubitChart(false)} />
            <div className="chart-modal-container">
              <div className="chart-modal-header">
                <h2>QUBIT VOLUME ANALYSIS</h2>
                <button onClick={() => setShowQubitChart(false)} className="chart-modal-close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                  CLOSE
                </button>
              </div>
              <div className="chart-modal-content">
                <QubitChart />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
